// Maps task data to the correct view component variant.
import { memo, Fragment } from 'react';
import {
  generateBreadcrumbPath,
  computeIndentLevels,
} from '@/features/tasks/utils/structure/taskHierarchy';
import TaskItem from '@/features/tasks/components/items/TaskItem';
import TaskInputInline from '@/features/tasks/components/forms/TaskInputInline';
import SubtaskInputWrapper from '@/features/tasks/components/atoms/SubtaskInputWrapper';
import TaskItemToday from '@/features/tasks/components/items/TaskItemToday';
import TaskItemOrganize from '@/features/tasks/components/items/TaskItemOrganize';
import TaskItemNested from '@/features/tasks/components/items/TaskItemNested';
import { NewTaskInputRow } from './NewTaskInputRow';
import { shouldShowSubtaskInputAfter } from './shouldShowSubtaskInputAfter';

const resolveShowPlayButton = (
  taskId,
  topActionableTaskId,
  showPlayButtonAlways,
) => {
  if (showPlayButtonAlways) return true;
  return topActionableTaskId !== null
    ? taskId === topActionableTaskId
    : undefined;
};

const VIEW_COMPONENTS = {
  today: TaskItemToday,
  organize: TaskItemOrganize,
  nested: TaskItemNested,
  completed: TaskItem,
};

const CONTENT_DEFAULTS = {
  taskListRef: null,
  handleFocus: () => {},
  handleBlur: () => {},
  tasks: [],
  tasksMap: new Map(),
  showBreadcrumbs: false,
  indentationType: 'none',
  view: 'default',
  topActionableTaskId: null,
  listTabIndex: 0,
  itemTabIndex: undefined,
  newTaskInput: {},
  editing: {},
  subtask: {},
};

const renderTaskRow = (args) => {
  const {
    task,
    index,
    TaskItemComponent,
    showBreadcrumbs,
    tasksMap,
    indentationType,
    tasks,
    topActionableTaskId,
    showPlayButtonAlways,
    editing,
    subtask,
  } = args;
  const breadcrumbData = showBreadcrumbs
    ? generateBreadcrumbPath(task, tasksMap)
    : null;
  const showSubtaskInput = shouldShowSubtaskInputAfter(
    task,
    index,
    tasks,
    subtask.addingSubtaskTo,
    tasksMap,
  );
  const subtaskAnchor =
    showSubtaskInput && subtask.addingSubtaskTo
      ? (tasksMap.get(subtask.addingSubtaskTo) ?? task)
      : task;
  const { subtaskInputLevel } = computeIndentLevels(
    subtaskAnchor,
    indentationType,
  );

  return (
    <>
      {editing.editingTaskId === task.id ? (
        <TaskInputInline
          initialTitle={task.title}
          onSave={editing.onSave}
          onCancel={editing.onCancel}
        />
      ) : (
        <TaskItemComponent
          task={task}
          tasks={tasks}
          breadcrumb={breadcrumbData}
          index={index}
          showPlayButton={resolveShowPlayButton(
            task.id,
            topActionableTaskId,
            showPlayButtonAlways,
          )}
        />
      )}
      {showSubtaskInput ? (
        <SubtaskInputWrapper
          level={subtaskInputLevel}
          onSave={subtask.onSave}
          onCancel={subtask.onCancel}
        />
      ) : null}
    </>
  );
};

/** @param {Object} props */
const TaskListContentBase = (props) => {
  const {
    taskListRef,
    handleFocus,
    handleBlur,
    tasks,
    tasksMap,
    showBreadcrumbs,
    indentationType,
    view,
    topActionableTaskId,
    showPlayButtonAlways,
    listTabIndex,
    itemTabIndex,
    newTaskInput,
    editing,
    subtask,
  } = { ...CONTENT_DEFAULTS, ...props };
  const TaskItemComponent = VIEW_COMPONENTS[view] ?? TaskItem;
  const usesListboxPattern =
    itemTabIndex === -1 && listTabIndex !== undefined && listTabIndex >= 0;

  return (
    <ul
      ref={taskListRef}
      tabIndex={listTabIndex}
      className="task-list"
      role={usesListboxPattern ? 'listbox' : undefined}
      onFocus={handleFocus}
      onBlur={handleBlur}
    >
      {tasks.map((task, index) => (
        <Fragment key={task.id}>
          {newTaskInput.index === index ? (
            <NewTaskInputRow
              onSave={newTaskInput.onSave}
              onCancel={newTaskInput.onCancel}
              triggeringKey={newTaskInput.triggeringKey}
              isPlanRail={view === 'plan'}
            />
          ) : null}
          {renderTaskRow({
            task,
            index,
            TaskItemComponent,
            showBreadcrumbs,
            tasksMap,
            indentationType,
            tasks,
            topActionableTaskId,
            showPlayButtonAlways,
            editing,
            subtask,
          })}
        </Fragment>
      ))}

      {newTaskInput.index === tasks.length ? (
        <NewTaskInputRow
          onSave={newTaskInput.onSave}
          onCancel={newTaskInput.onCancel}
          triggeringKey={newTaskInput.triggeringKey}
          isPlanRail={view === 'plan'}
        />
      ) : null}
    </ul>
  );
};

const TaskListContent = memo(TaskListContentBase);
TaskListContent.displayName = 'TaskListContent';

export default TaskListContent;
